/**
 * useItemSelection Hook
 * Manages item selection state for bulk operations
 * Extracted from InventoryView to reduce file size and improve testability
 */

import { useState, useCallback } from 'react';

export interface SavedItem {
  id: number;
  [key: string]: any;
}

export interface UseItemSelectionReturn {
  // State
  selectedIds: Set<number>;
  isSelectMode: boolean;
  
  // Actions
  toggleSelection: (id: number) => void;
  selectAll: (items: SavedItem[]) => void;
  clearSelection: () => void;
  enterSelectMode: () => void;
  exitSelectMode: () => void;
  isSelected: (id: number) => boolean;
  getSelectedCount: () => number;
  getSelectedItems: (items: SavedItem[]) => SavedItem[];
}

/**
 * Custom hook for managing item selection state
 * Used for bulk operations like delete, add to collection, etc.
 * 
 * @returns Selection state and actions
 */
export function useItemSelection(): UseItemSelectionReturn {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isSelectMode, setIsSelectMode] = useState(false);

  /**
   * Toggle selection for a single item
   */
  const toggleSelection = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  /**
   * Select all items
   */
  const selectAll = useCallback((items: SavedItem[]) => {
    setSelectedIds(new Set(items.map(item => item.id)));
  }, []);

  /**
   * Clear all selections
   */
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  /**
   * Enter select mode
   */
  const enterSelectMode = useCallback(() => {
    setIsSelectMode(true);
  }, []);

  /**
   * Exit select mode and clear selections
   */
  const exitSelectMode = useCallback(() => {
    setIsSelectMode(false);
    setSelectedIds(new Set());
  }, []);

  /**
   * Check if an item is selected
   */
  const isSelected = useCallback((id: number): boolean => {
    return selectedIds.has(id);
  }, [selectedIds]);

  /**
   * Get count of selected items
   */
  const getSelectedCount = useCallback((): number => {
    return selectedIds.size;
  }, [selectedIds]);

  /**
   * Get array of selected items
   */
  const getSelectedItems = useCallback((items: SavedItem[]): SavedItem[] => {
    return items.filter(item => selectedIds.has(item.id));
  }, [selectedIds]);

  return {
    // State
    selectedIds,
    isSelectMode,
    
    // Actions
    toggleSelection,
    selectAll,
    clearSelection,
    enterSelectMode,
    exitSelectMode,
    isSelected,
    getSelectedCount,
    getSelectedItems,
  };
}
